import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui/Button';

import { useResyncWall } from '../../../../queries/videoWall';

/**
 * "Changes saved — N wall(s) playing this playlist, Refresh to apply" banner.
 *
 * Driven by the `affected_walls` the save endpoints return (walls whose
 * current_playlist_id == the just-saved playlist AND state=playing). Saving NEVER
 * auto-interrupts live screens (restaurant rule) — the operator chooses when to
 * re-buffer, per wall or all at once.
 *
 * Each affected entry is one VideoWall (its own internally-synced unit; groups are
 * fan-out, not cross-wall frame-synced), so a per-wall resync IS the correct
 * coordinated refresh — group_name is shown only as context. Pure UI; the resync
 * itself reuses the verified POST /video-walls/{id}/resync path.
 *
 * Props:
 *   walls:     [{ id, name, group_id, group_name }]
 *   onDismiss: () => void
 */

// One affected wall. Owns its own resync mutation (a hook per row is the only
// React-legal way to give each wall its own button) and reacts to "Refresh all".
function WallRefreshRow({ wall, triggerAll }) {
  const { t } = useTranslation();
  const resync = useResyncWall(wall.id);
  const [done, setDone] = useState(false);

  const run = () => {
    if (done || resync.isLoading) return;
    resync.mutateAsync().then(() => setDone(true)).catch(() => {});
  };

  // "Refresh all" bumps triggerAll; each not-yet-refreshed row fires once.
  useEffect(() => {
    if (triggerAll > 0) run();
  }, [triggerAll]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="d-flex align-items-center gap-2 py-1">
      <i className="pi pi-desktop" style={{ color: 'var(--pp-text-secondary)', fontSize: 14 }} />
      <span className="small" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{wall.name}</span>
      {wall.group_name && (
        <span className="small" style={{ color: 'var(--pp-text-secondary)' }}>· {wall.group_name}</span>
      )}
      <span className="ms-auto">
        {done ? (
          <span className="small d-inline-flex align-items-center gap-1" style={{ color: 'var(--pp-success)' }}>
            <i className="pi pi-check-circle" /> {t('video_wall.live_edit.refreshed')}
          </span>
        ) : (
          <Button label={t('video_wall.refresh')} icon="pi pi-sync" size="small" outlined
            loading={resync.isLoading} onClick={run} />
        )}
      </span>
    </div>
  );
}

export default function LiveContentBanner({ walls, onDismiss }) {
  const { t } = useTranslation();
  const [triggerAll, setTriggerAll] = useState(0);

  if (!Array.isArray(walls) || walls.length === 0) return null;

  return (
    <div
      className="p-3 mb-3"
      style={{
        borderRadius: 'var(--pp-radius-lg)',
        background: 'var(--pp-bg-elevated)',
        borderInlineStart: '4px solid var(--pp-warning, #f59e0b)',
        border: '1px solid var(--pp-border)',
      }}
    >
      <div className="d-flex align-items-start gap-2 mb-2">
        <i className="pi pi-info-circle" style={{ color: 'var(--pp-warning, #f59e0b)', fontSize: 18, marginTop: 2 }} />
        <div className="flex-grow-1">
          <strong style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.live_edit.saved_title')}</strong>
          <div className="small" style={{ color: 'var(--pp-text-secondary)' }}>
            {t('video_wall.live_edit.walls_need_refresh', { n: walls.length })}
          </div>
        </div>
        <div className="d-flex gap-2">
          {walls.length > 1 && (
            <Button label={t('video_wall.live_edit.refresh_all')} icon="pi pi-sync" size="small"
              onClick={() => setTriggerAll((x) => x + 1)} />
          )}
          {onDismiss && (
            <Button icon="pi pi-times" rounded text size="small"
              aria-label={t('video_wall.cancel')} onClick={onDismiss} />
          )}
        </div>
      </div>
      <div className="ps-4">
        {walls.map((w) => <WallRefreshRow key={w.id} wall={w} triggerAll={triggerAll} />)}
      </div>
    </div>
  );
}
